import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './ProjectCard.css';
import cardBackground from '../../../../assets/cardBackground.jpg';
import { useProject } from '../../../../contexts/ProjectContext';
import { getVirtualTourUrl, getVirtualTourCode, getProjectVirtualTours, getVirtualTourUrlByCode } from '../../../../utils/virtualTourMapping';
import { useEC2Loading } from '../../../../contexts/EC2LoadingContext';
import { useEC2InstanceStatus } from '../../../../contexts/EC2InstanceStatusContext';
import EC2LoadingBar from './EC2LoadingBar';
import ManageProjectUsersModal from './ManageProjectUsersModal';
import Launch3DViewerModal from './Launch3DViewerModal';
import Stop3DViewerModal from './Stop3DViewerModal';
import RenameProjectModal from './RenameProjectModal';
import LeaveProjectModal from './LeaveProjectModal';
import { authHeaders } from '../../../../utils/authHeader';
import axios from 'axios';
import viewerPresenceService from '../../../../services/viewerPresenceService';
import ActiveViewersBadge from '../../ActiveViewersBadge/ActiveViewersBadge';
import ec2InstanceService from '../../../../services/ec2InstanceService';
import { baseApi } from '../../../../API/api';

const ProjectCard = ({ project, onProjectSelect, onViewProject, formatDate, user, customRedirectPath, cardIndex = 0 }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [showEC2Loading, setShowEC2Loading] = useState(false);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeavingProject, setIsLeavingProject] = useState(false);
  const menuRef = useRef(null);
  const thumbnailImgRef = useRef(null);
  const { stopLoading, isProjectLoading, startLoading, getLoadingProject } = useEC2Loading();
  
  // Check if this project is loading from global context
  const isGloballyLoading = isProjectLoading(project.Id);
  const loadingState = getLoadingProject(project.Id);
  const loadingProgress = loadingState?.progress || 0;
  const isInTransition = isGloballyLoading && loadingProgress < 100;
  
  // Get EC2 instance status
  const { getInstanceStatus, isInstanceRunning, isInstanceStopped, hasInstanceError } = useEC2InstanceStatus();
  const instanceStatus = getInstanceStatus(String(project.Id));
  const isRunning = isInstanceRunning(String(project.Id));
  const isStopped = isInstanceStopped(String(project.Id));
  const hasError = hasInstanceError(String(project.Id));
  const isStopping = instanceStatus?.state === 'stopping' || instanceStatus?.state === 'shutting-down';
  
  // Use project context utilities
  const { isProjectExpired, canViewProject, canAccessProjectFiles, formatFileSize, loadProjects } = useProject();

  const isExpired = isProjectExpired(project);
  const canView = canViewProject(project);
  const canAccessFiles = canAccessProjectFiles(project);
  const normalizedProjectType = String(project?.ProjectType || '').toLowerCase();
  const isRsaExistingProject = normalizedProjectType === 'rsa-existing' || normalizedProjectType === 'rsa-exiting';
  const isRsaModelProject = normalizedProjectType === 'rsa-model' || normalizedProjectType === 'rsaa-model';
  const isRsaProjectType = isRsaExistingProject || isRsaModelProject;
  const isUnrealModelProject = normalizedProjectType === 'unreal-model';
  const shouldSuppressStoppedIndicator = isUnrealModelProject || isRsaProjectType;
  const currentUserEmail = useMemo(() => {
    const fromUserObject = user?.attributes?.email || user?.email || user?.Email || null;
    const fromSession = sessionStorage.getItem('userEmail');
    const email = fromUserObject || fromSession || null;
    return email ? String(email).trim().toLowerCase() : null;
  }, [user]);

  const handleImageError = () => {
    console.warn(`⚠️ ProjectCard: Image failed to load for project ${project.Id}, retry count: ${retryCount}`);
    
    // Retry up to 2 times with exponential backoff
    if (retryCount < 2) {
      const delay = (retryCount + 1) * 1000; // 1s, 2s
      console.log(`🔄 Retrying image load in ${delay}ms...`);
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false);
        setImageLoading(true);
      }, delay);
    } else {
      console.error(`❌ ProjectCard: Image failed after ${retryCount + 1} attempts for project ${project.Id}`);
      setImageError(true);
      setImageLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    setRetryCount(0); // Reset retry count on successful load
  };

const ec2IndicatorStatusClass =
    hasError
      ? 'error'
      : isGloballyLoading && loadingProgress < 100
        ? 'transition'
        : isGloballyLoading && loadingProgress >= 100
          ? 'running'
          : isRunning && !isGloballyLoading
            ? 'running'
            : isStopped
              ? (shouldSuppressStoppedIndicator ? 'unknown' : 'stopped')
              : 'unknown';

  const ec2IndicatorTitle =
    hasError
      ? (instanceStatus?.message || 'Can\'t find instance match to project')
      : isGloballyLoading && loadingProgress < 100
        ? `3D viewer starting... (${loadingProgress}%)`
        : isGloballyLoading && loadingProgress >= 100
          ? `3D viewer ready (${loadingProgress}%)`
          : isRunning
            ? `3D viewer ${instanceStatus?.state || 'running'}`
            : `3D viewer ${instanceStatus?.state || 'unknown'}`;

  const canOpenProjectFiles = canAccessFiles && !isExpired;

  const handleOpenProjectFiles = () => {
    if (!canOpenProjectFiles) return;
    navigate(`/projects/${project.Id}?rsa_view=files`);
  };

  const handleOpenProjectUpload = () => {
    if (!canOpenProjectFiles) return;
    if (isRsaProjectType) {
      navigate(`/projects/${project.Id}?rsa_view=analysis&rsa_tab=upload`);
    } else {
      navigate(`/projects/${project.Id}?rsa_view=files`);
    }
  };

  // Reset image error and loading state when project changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
    setRetryCount(0);
  }, [project.Id, project.ThumbnailURL]);

  // Memoize thumbnail URL and keep it stable for browser/CDN cache reuse.
  const thumbnailUrl = useMemo(() => {
    if (!project.ThumbnailURL || project.ThumbnailURL === 'null' || project.ThumbnailURL.trim() === '') {
      return null;
    }

    if (retryCount <= 0) {
      return project.ThumbnailURL;
    }

    const separator = project.ThumbnailURL.includes('?') ? '&' : '?';
    return `${project.ThumbnailURL}${separator}retry=${retryCount}`;
  }, [project.ThumbnailURL, retryCount]);

  // Cached images often complete before `onLoad` runs; `display:none` also blocks decode/lazy load.
  useLayoutEffect(() => {
    const el = thumbnailImgRef.current;
    if (!el || !thumbnailUrl) return;
    if (el.complete && el.naturalWidth > 0) {
      setImageLoading(false);
    }
  }, [thumbnailUrl, project.Id]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      // Check if there's enough space below for the menu
      if (menuRef.current) {
        const buttonRect = menuRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const estimatedMenuHeight = 300; // Approximate height of the menu
        
        // If not enough space below, open upward
        setMenuOpensUpward(spaceBelow < estimatedMenuHeight);
      }
    }
    
    setShowMenu(!showMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleStop3DViewer = async () => {
    try {
      // Stop the loading if it's currently loading
      if (isGloballyLoading) {
        stopLoading(project.Id);
      }

      // Call the stop instance API
      const result = await ec2InstanceService.stopInstance(project.Id);
      
      if (result.success) {
        console.log(`✅ Successfully stopped 3D Viewer for project ${project.Id}`);
        // Modal is already closed, no need to show alert
      } else {
        console.error(`❌ Failed to stop 3D Viewer for project ${project.Id}:`, result.message);
        alert(`Failed to stop 3D Viewer: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`❌ Error stopping 3D Viewer for project ${project.Id}:`, error);
      alert(`Error stopping 3D Viewer: ${error.message || 'Unknown error'}`);
    }
  };

  const handleRenameProject = async (newName) => {
    const trimmedName = String(newName || '').trim();
    if (!trimmedName) {
      return;
    }

    setRenameError('');
    setIsRenaming(true);

    try {
      const response = await fetch(`${baseApi}/api/projects/${project.Id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmedName })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }

      if (!response.ok || payload?.success === false) {
        const backendError = payload?.error || payload?.message;
        throw new Error(backendError || 'Failed to rename project');
      }

      await loadProjects(true);
      setShowRenameModal(false);
      setRenameError('');
    } catch (error) {
      console.error(`❌ Error renaming project ${project.Id}:`, error);
      setRenameError(error.message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    switch (action) {
      case 'files':
        handleOpenProjectFiles();
        break;
      case 'settings':
        // TODO: Navigate to project settings
        console.log('Project settings:', project.Id);
        break;
      case 'share':
        // TODO: Navigate to project sharing
        console.log('Share project:', project.Id);
        break;
      case 'rename':
        setRenameError('');
        setShowRenameModal(true);
        break;
      case 'download':
        // TODO: Download project shortcut
        window.location.href = `/api/shortcut/project?projectId=${project.Id}`;
        break;
      case 'downloadVTour':
        // TODO: Download virtual tour shortcut
        window.location.href = `/api/shortcut/vtour?projectId=${project.Id}`;
        break;
      case 'leave':
        setShowLeaveModal(true);
        break;
      case 'copy':
        // TODO: Copy project model
        console.log('Copy project model:', project.Id);
        break;
      case 'paste':
        // TODO: Paste project model
        console.log('Paste project model:', project.Id);
        break;
      case 'manageUsers':
        setShowManageUsersModal(true);
        break;
      case 'stop3DViewer':
        setShowStopModal(true);
        break;
      default:
        break;
    }
  };

  const handleLeaveProject = async () => {
    setIsLeavingProject(true);
    try {
      await axios.post(`${baseApi}/api/projects/${project.Id}/leave`, {
        email: user?.email
      }, { headers: await authHeaders() });
      setShowLeaveModal(false);
      loadProjects();
    } catch (error) {
      console.error('Failed to leave project:', error);
    } finally {
      setIsLeavingProject(false);
    }
  };

  return (
    <div className={`project-card gallery-view ${isExpired ? 'expired' : ''}`}>
      {/* EC2 Loading Bar - Show if globally loading */}
      {isGloballyLoading && (
        <EC2LoadingBar
          projectId={project.Id}
          onComplete={() => {
            // URL opening is now handled inside EC2LoadingBar after 1.5 minute delay
            console.log(`✅ EC2 instance ready for project ${project.Id}`);
          }}
          onError={(error) => {
            console.error('EC2 loading bar error:', error);
            console.error('Error details:', {
              message: error.message,
              projectId: error.projectId,
              instanceId: error.instanceId,
              instanceName: error.instanceName,
              backendResponse: error.backendResponse,
              troubleshooting: error.troubleshooting
            });
            stopLoading(project.Id);

            // Transient GPU-capacity exhaustion: the backend retried for a g6 host for
            // ~3 min and AWS still had none free in this AZ. This is recoverable, so offer
            // a retry instead of a dead-end alert (the retry re-runs the whole launch).
            if (error.isCapacityTimeout) {
              const retry = window.confirm(
                'No 3D viewer server is available right now.\n\n' +
                'GPU capacity in this region is temporarily full. It usually frees up ' +
                'within a few minutes.\n\nTry again now?'
              );
              if (retry) {
                startLoading(project.Id, project.Name);
              }
              return;
            }

            // Extract the most helpful error message
            let displayMessage = error.message || 'Unknown error';
            
            // If we have backend response with detailed error, use that
            if (error.backendResponse?.error) {
              const backendError = error.backendResponse.error;
              // Check for specific AWS errors and provide user-friendly messages
              if (backendError.includes('VcpuLimitExceeded')) {
                displayMessage = 'AWS vCPU limit exceeded. You have reached your EC2 instance vCPU limit. Please contact AWS support to request an increase, or stop other running instances.';
              } else if (backendError.includes('InsufficientInstanceCapacity')) {
                displayMessage = 'Insufficient instance capacity. The requested instance type is not available in this region. Please try again later.';
              } else if (backendError.includes('InstanceLimitExceeded')) {
                displayMessage = 'Instance limit exceeded. You have reached the maximum number of running instances. Please stop other instances before starting a new one.';
              } else {
                // Use a cleaned version of the backend error
                displayMessage = backendError.replace(/An error occurred \([^)]+\) when calling the [^:]+: /, '');
              }
            }
            
            // Show error message with troubleshooting tips if available
            let errorMsg = `Failed to start 3D Viewer instance:\n\n${displayMessage}`;
            
            // Check all possible locations for troubleshooting information
            const troubleshooting = error.troubleshooting || 
                                  error.backendResponse?.troubleshooting || 
                                  error.response?.data?.troubleshooting;
            
            if (troubleshooting) {
              const tips = Array.isArray(troubleshooting) ? troubleshooting : [troubleshooting];
              errorMsg += '\n\nTroubleshooting:\n' + tips.map((tip, idx) => `${idx + 1}. ${tip}`).join('\n');
            }
            
            // Add instance details if available
            if (error.instanceId || error.instanceName) {
              errorMsg += `\n\nInstance: ${error.instanceName || 'Unknown'} (${error.instanceId || 'Unknown ID'})`;
            }
            
            // Add link to AWS support for vCPU limit issues
            if (error.backendResponse?.error?.includes('VcpuLimitExceeded')) {
              errorMsg += '\n\nTo request a vCPU limit increase, visit: https://aws.amazon.com/contact-us/ec2-request';
            }
            
            alert(errorMsg);
          }}
          onStopping={() => {
            console.log('EC2 instance is stopping, showing message and hiding loading bar');
            stopLoading(project.Id);
            // Show message to user about stopping state
            alert('3D Viewer is shutting down. Please wait a few minutes for it to fully stop, then try again.');
          }}
          onSuccess={async () => {
            const loadingProject = getLoadingProject(project.Id);
            const initiatingUserEmail = loadingProject?.initiatingUserEmail
              ? String(loadingProject.initiatingUserEmail).trim().toLowerCase()
              : null;
            const launchedByThisClient = loadingProject?.launchedByThisClient === true;
            const shouldRedirect = Boolean(
              launchedByThisClient ||
              (currentUserEmail &&
                initiatingUserEmail &&
                currentUserEmail === initiatingUserEmail)
            );

            if (!shouldRedirect) {
              console.log(`Skipping redirect for project ${project.Id}: current user is not the launch initiator`);
              return;
            }

            // Navigate to the project viewer page when loading completes
            if (!isExpired && canView) {
              console.log(`Navigating to project viewer for project ${project.Id}...`);

              try {
                // Register overlay click with backend (await to ensure it completes)
                // This tracks the user as active in the viewer
                await viewerPresenceService.registerOverlayClick(project.Id);
                console.log(`Overlay click registered for project ${project.Id}`);
              } catch (error) {
                console.warn(`Failed to register overlay click for project ${project.Id}:`, error);
                // Continue with navigation even if registration fails
              }

              // Navigate to the viewer page (without fullscreen parameter)
              console.log(`Navigating to /projects/${project.Id}/view`);
              navigate(`/projects/${project.Id}/view`);
            }
          }}
        />
      )}
      
      <div
        className="project-thumbnail"
        onClick={handleOpenProjectUpload}
        style={{
          cursor: canOpenProjectFiles ? 'pointer' : 'default',
          ...(!thumbnailUrl || imageError ? {
            backgroundImage: `url(${cardBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : {})
        }}
      >
        <ActiveViewersBadge projectId={project.Id} variant="thumbnail" />

        {thumbnailUrl && !imageError ? (
          <>
            <img 
              ref={thumbnailImgRef}
              src={thumbnailUrl} 
              alt={project.Name}
              onError={handleImageError}
              onLoad={handleImageLoad}
              style={{ 
                opacity: imageLoading ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out'
              }}
              loading={cardIndex < 6 ? 'eager' : 'lazy'}
              decoding="async"
            />
            {imageLoading && (
              <div className="project-thumbnail-loading" />
            )}
          </>
        ) : null}
        {!isRsaProjectType && (
          <div 
            className="project-3d-overlay"
            onClick={async (e) => {
              e.stopPropagation();
              if (!isExpired && canView) {
                try {
                  await viewerPresenceService.registerOverlayClick(project.Id);
                } catch (error) {
                  console.warn(`Failed to register overlay click for project ${project.Id}:`, error);
                }

                if (isStopping && !isGloballyLoading) {
                  alert('3D Viewer is currently shutting down. Please wait a few minutes for it to fully stop, then try again.');
                  return;
                }

                if (isStopped && instanceStatus && !isGloballyLoading && !isStopping) {
                  setShowLaunchModal(true);
                } else {
                  navigate(`/projects/${project.Id}/view`);
                }
              }
            }}
            style={{ 
              cursor: (!isExpired && canView) ? 'pointer' : 'default',
              pointerEvents: (!isExpired && canView) ? 'auto' : 'none'
            }}
          >
            {/* Open in new tab button */}
            {!isExpired && canView && (
              <button
                className="project-3d-open-new-tab"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log(`🔗 Opening project ${project.Id} in new tab`);
                  
                  // CRITICAL: window.open() must be called synchronously in response to user action
                  // Otherwise browser popup blocker will block it
                  const viewUrl = `${window.location.origin}/projects/${project.Id}/view`;
                  console.log(`🚀 Opening absolute URL: ${viewUrl}`);
                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                  
                  // Register overlay click with backend AFTER opening window (non-blocking)
                  // This prevents popup blocker from blocking the window.open()
                  viewerPresenceService.registerOverlayClick(project.Id)
                    .then(() => {
                      console.log(`✅ Overlay click registered for project ${project.Id} (new tab)`);
                    })
                    .catch((error) => {
                      console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                    });
                }}
                title="Open in new tab"
                aria-label="Open in new tab"
              >
                <i className="fas fa-external-link-alt"></i>
              </button>
            )}
            <span className="project-3d-icon">explore</span>
            <span className="project-3d-text">3D View</span>
          </div>
        )}
        {isRsaProjectType && (
          <div
            className="project-3d-overlay"
            onClick={(e) => {
              e.stopPropagation();
              if (!isExpired && canOpenProjectFiles) {
                handleOpenProjectUpload();
              }
            }}
            style={{
              cursor: (!isExpired && canOpenProjectFiles) ? 'pointer' : 'default',
              pointerEvents: (!isExpired && canOpenProjectFiles) ? 'auto' : 'none'
            }}
          >
            <i className="fa-solid fa-pen-to-square project-3d-icon-fa"></i>
            <span className="project-3d-text">Create RSA</span>
          </div>
        )}

        {isExpired && (
          <div className="expired-overlay">
            <i className="fas fa-lock"></i>
            <h4>Project Expired</h4>
            <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
            <button 
              className="leave-btn"
              onClick={(e) => handleMenuAction('leave', e)}
            >
              Leave Project
            </button>
          </div>
        )}
        
        <div className={`project-card-menu ${showMenu ? 'show' : ''}`} ref={menuRef}>
          <button 
            className="project-menu-trigger" 
            onClick={handleMenuToggle}
            disabled={isExpired}
          >
            <div className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
          
          {showMenu && (
            <div className={`project-menu-dropdown ${menuOpensUpward ? 'open-upward' : ''}`}>
              {isExpired ? (
                <div className="expired-menu-content">
                  <h4>Project Expired</h4>
                  <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
                  <button 
                    className="leave-btn"
                    onClick={(e) => handleMenuAction('leave', e)}
                  >
                    Leave Project
                  </button>
                </div>
              ) : (
                <ul className="project-menu-list" role="menu">
                  <li role="none">
                    <button onClick={(e) => handleMenuAction('view', e)} role="menuitem">
                      <i className="fas fa-eye"></i>
                      View Project
                    </button>
                  </li>
                  <li role="none">
                    <button onClick={(e) => handleMenuAction('files', e)} role="menuitem">
                      <i className="fas fa-folder-open"></i>
                      Project Files
                    </button>
                  </li>
                  <li role="none">
                    <button onClick={(e) => handleMenuAction('manageUsers', e)} role="menuitem">
                      <i className="fas fa-users-cog"></i>
                      Manage Project Users
                    </button>
                  </li>
                  <li role="none">
                    <button onClick={(e) => handleMenuAction('rename', e)} role="menuitem">
                      <i className="fas fa-edit"></i>
                      Rename Project
                    </button>
                  </li>
                  {!isRsaProjectType && (
                    <li role="none">
                      <button onClick={(e) => handleMenuAction('vtour', e)} role="menuitem">
                        <i className="fas fa-vr-cardboard"></i>
                        Virtual Tour
                      </button>
                    </li>
                  )}
                  {!isRsaProjectType && (
                    <li role="none">
                      <button onClick={(e) => handleMenuAction('stop3DViewer', e)} role="menuitem">
                        <i className="fas fa-stop"></i>
                        Stop 3D Viewer
                      </button>
                    </li>
                  )}
                  <li role="none">
                    <button onClick={(e) => handleMenuAction('leave', e)} role="menuitem" className="danger">
                      <i className="fas fa-sign-out-alt"></i>
                      Leave Project
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="project-info">
        <div className="project-title-container">
          <h3 className="project-title">{project.Name}</h3>
          {/* EC2 Instance Status Indicator - hide for all RSA project types */}
          {(!isRsaProjectType && (instanceStatus || isGloballyLoading)) && (
            <div
              className={`ec2-status-indicator ${ec2IndicatorStatusClass}`}
              title={ec2IndicatorTitle}
            >
              {hasError ? (
                <i className="fas fa-exclamation-triangle ec2-status-warning-icon"></i>
              ) : (
                <span className="ec2-status-dot"></span>
              )}
            </div>
          )}
        </div>
        
        <div className="project-meta">
          <div className="project-meta-left">
            <span
              className={`project-type-badge project-type-badge--${(project.ProjectType || 'visualization').toLowerCase()}`}
              title={`Project type: ${(project.ProjectType || 'visualization').toUpperCase()}`}
            >
              {(project.ProjectType || 'visualization').toUpperCase()}
            </span>
            {project.UserRoleId < 2 && (
              <span className="expiry">Exp. Date: {project.Expiration ? (project.Expiration instanceof Date ? project.Expiration.toLocaleDateString() : new Date(project.Expiration).toLocaleDateString()) : 'N/A'}</span>
            )}
          </div>
          
          <div className="project-meta-bottom">
            <span className="role">ID: {project.Id}</span>
            
            <div className="project-actions">
              {canAccessFiles && (
                <button 
                  className="action-link files"
                  onClick={handleOpenProjectFiles}
                  disabled={isExpired}
                >
                  <i className="fas fa-folder-open"></i>
                  Project Files
                </button>
              )}
              
              {/* Virtual Tour only for non-RSA project types */}
              {!isRsaProjectType && (
                <VirtualTourButton 
                  project={project} 
                  isExpired={isExpired}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      
      
      <ManageProjectUsersModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showManageUsersModal}
        onClose={() => setShowManageUsersModal(false)}
      />
      
      <Launch3DViewerModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showLaunchModal}
        onClose={() => setShowLaunchModal(false)}
        onLaunchSuccess={async () => {
          // Check instance state before starting
          const currentStatus = getInstanceStatus(String(project.Id));
          const currentIsStopping = currentStatus?.state === 'stopping' || currentStatus?.state === 'shutting-down';
          
          if (currentIsStopping) {
            console.log(`⚠️ Cannot start loading - instance is stopping for project ${project.Id}`);
            alert('3D Viewer is currently shutting down. Please wait a few minutes for it to fully stop, then try again.');
            setShowLaunchModal(false);
            return;
          }
          
          // Start the loading bar which will handle the instance launch
          console.log(`🚀 Starting EC2 loading for project ${project.Id}`);
          startLoading(project.Id, project.Name);
        }}
      />
      
      <Stop3DViewerModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onStopSuccess={handleStop3DViewer}
      />

      <LeaveProjectModal
        isOpen={showLeaveModal}
        projectName={project.Name}
        isSubmitting={isLeavingProject}
        onCancel={() => setShowLeaveModal(false)}
        onConfirm={handleLeaveProject}
      />

      <RenameProjectModal
        projectName={project.Name}
        initialName={project.Name}
        isOpen={showRenameModal}
        isSubmitting={isRenaming}
        error={renameError}
        onCancel={() => {
          if (isRenaming) {
            return;
          }
          setShowRenameModal(false);
          setRenameError('');
        }}
        onConfirm={handleRenameProject}
      />
    </div>
  );
};

// Virtual Tour Dropdown Component
const VirtualTourButton = ({ project, isExpired }) => {
  const [virtualTours, setVirtualTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState({});

  // Load virtual tours on component mount with retry logic
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second
    
    const loadVirtualTours = async (attempt = 0) => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        const tours = await getProjectVirtualTours(project.Id);
        
        if (!isMounted) return;
        
        // Ensure we got valid data
        if (Array.isArray(tours)) {
          setVirtualTours(tours);
        } else {
          console.warn(`⚠️ VirtualTourButton: Invalid tours data for project ${project.Id}:`, tours);
          setVirtualTours([]);
        }
      } catch (error) {
        console.error(`❌ VirtualTourButton: Failed to load tours for project ${project.Id} (attempt ${attempt + 1}):`, error);
        
        // Retry if we haven't exceeded max retries
        if (attempt < MAX_RETRIES && isMounted) {
          retryCount++;
          console.log(`🔄 VirtualTourButton: Retrying in ${RETRY_DELAY}ms... (${retryCount}/${MAX_RETRIES})`);
          setTimeout(() => {
            if (isMounted) {
              loadVirtualTours(attempt + 1);
            }
          }, RETRY_DELAY);
          return; // Don't set loading to false yet
        }
        
        // If all retries failed, set empty array
        if (isMounted) {
          setVirtualTours([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadVirtualTours();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [project.Id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideToggle = dropdownRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideToggle && !clickedInsideMenu) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleVirtualTourClick = (e, tour) => {
    e.stopPropagation();
    const virtualTourUrl = getVirtualTourUrlByCode(tour.code);
    console.log('🔗 Opening virtual tour URL:', virtualTourUrl, 'for tour:', tour.vtour_name);
    window.open(virtualTourUrl, '_blank', 'noopener,noreferrer');
    setShowDropdown(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isExpired && virtualTours.length > 0) {
      // Check available space and determine if dropdown should open upwards
      if (!showDropdown && toggleRef.current) {
        const buttonRect = toggleRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Estimate dropdown height (each item ~56px + padding)
        const estimatedDropdownHeight = Math.min(virtualTours.length * 56 + 32, 400);
        
        const shouldOpenUpwards = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;

        const topPosition = shouldOpenUpwards
          ? buttonRect.top + window.scrollY - estimatedDropdownHeight - 8
          : buttonRect.bottom + window.scrollY + 8;

        setDropdownStyles({
          position: 'absolute',
          top: topPosition,
          left: buttonRect.left + window.scrollX,
          minWidth: Math.max(buttonRect.width, 280),
          maxHeight: estimatedDropdownHeight
        });
      }
      setShowDropdown(!showDropdown);
    } else if (!isExpired && virtualTours.length === 1) {
      // If only one tour, open it directly
      handleVirtualTourClick(e, virtualTours[0]);
    }
  };

  // Don't render if no tours available (and not loading)
  if (!isLoading && virtualTours.length === 0) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <button 
        className="action-link vtour"
        disabled={true}
        title="Loading virtual tours..."
      >
        <i className="fas fa-spinner fa-spin"></i>
        Loading...
      </button>
    );
  }

  // If only one tour, render as a simple button
  if (virtualTours.length === 1) {
    return (
      <button 
        className="action-link vtour"
        onClick={(e) => handleVirtualTourClick(e, virtualTours[0])}
        disabled={isExpired || isLoading}
        title={virtualTours[0].vtour_name}
      >
        <i className="fas fa-vr-cardboard"></i>
        {isLoading ? 'Loading...' : "Virtual Tour"}
      </button>
    );
  }

  // Render dropdown for multiple tours

  const renderDropdownMenu = () => (
    <div
      className="vtour-dropdown-menu"
      style={dropdownStyles}
      ref={menuRef}
    >
      {virtualTours.map((tour, index) => (
        <div 
          key={index}
          className="vtour-dropdown-item"
          onClick={(e) => handleVirtualTourClick(e, tour)}
        >
          <i className="fas fa-vr-cardboard"></i>
          <span className="vtour-name">{tour.vtour_name}</span>
          <span className="vtour-code">({tour.code})</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="vtour-dropdown-container" ref={dropdownRef}>
      <button 
        className={`action-link vtour vtour-dropdown-toggle ${showDropdown ? 'active' : ''}`}
        onClick={toggleDropdown}
        disabled={isExpired}
        title="Virtual Tours"
        ref={toggleRef}
      >
        <i className="fas fa-vr-cardboard"></i>
        {`Virtual Tours`}
        <i className={`fas fa-chevron-down dropdown-arrow ${showDropdown ? 'rotate' : ''}`}></i>
      </button>
      
      {showDropdown && !isExpired && (
        typeof document === 'undefined'
          ? renderDropdownMenu()
          : createPortal(renderDropdownMenu(), document.body)
      )}
    </div>
  );
};

export default ProjectCard;

